"use client"

import { ChevronDownMini } from "@medusajs/icons"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { clx } from "@modules/common/components/ui"
import { useEffect, useRef, useState } from "react"

export type NavCategory = {
  id: string
  name: string
  handle: string
  children: {
    id: string
    name: string
    handle: string
  }[]
}

const CategoriesDropdown = ({ categories }: { categories: NavCategory[] }) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false)
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open])

  if (!categories.length) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className="relative h-full flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="true"
        className={clx(
          "flex items-center gap-x-1 h-full transition-colors hover:text-white",
          open && "text-white"
        )}
        data-testid="nav-categories-button"
      >
        Categories
        <ChevronDownMini
          aria-hidden="true"
          className={clx("transition-transform duration-150", open && "rotate-180")}
        />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full z-50 w-max min-w-[280px] max-w-[640px] rounded-b-rounded border border-t-0 border-grey-80 bg-grey-90 p-6 shadow-elevation-card-hover"
          data-testid="nav-categories-panel"
        >
          <ul className="grid grid-cols-2 gap-x-10 gap-y-6">
            {categories.map((category) => (
              <li key={category.id}>
                <LocalizedClientLink
                  href={`/categories/${category.handle}`}
                  className="txt-compact-small-plus font-semibold uppercase tracking-wide text-white hover:text-amber-400 transition-colors"
                  onClick={() => setOpen(false)}
                  data-testid={`nav-category-${category.handle}`}
                >
                  {category.name}
                </LocalizedClientLink>
                {category.children.length > 0 && (
                  <ul className="mt-2 flex flex-col gap-y-1">
                    {category.children.map((child) => (
                      <li key={child.id}>
                        <LocalizedClientLink
                          href={`/categories/${child.handle}`}
                          className="text-grey-30 hover:text-white transition-colors"
                          onClick={() => setOpen(false)}
                          data-testid={`nav-category-${child.handle}`}
                        >
                          {child.name}
                        </LocalizedClientLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default CategoriesDropdown
